import { memo } from 'react';
import { useTranslation } from 'react-i18next'
import CampaignTable from '../../../../shared/CampaignTable';
import { usePrizeFundFormatting } from '../../../../../hooks/data/usePrizeFundFormatting';
import { useCampaignStatus } from '../../../../../hooks/data/campaignStatus/useCampaignStatus';
import { useGameStartActions } from 'features/games';
import Image from '../../../../../../../shared/components/Image';
import LoadingIndicator from '../../../../../../../shared/components/LoadingIndicator';
import tableStyles from '../../../../shared/CampaignTable/CampaignTable.module.css';
import { useSelectedCampaign, type IPrizePopWin, PRIZE_POP_CAMPAIGN_TYPE } from 'features/campaigns';
import { useCampaignWinsQuery } from '../../../../../hooks/data/useCampaignsQuery';
import { useSettings } from 'store/app/appStore';

import styles from './WinnersTab.module.css';
import { getMonthAbbr } from 'shared/utils/dateHelpers';

const TABLE_COLUMNS = [
  {
    name: 'Players',
    key: 'campaigns.player',
    cellClassName: '',
  },
  {
    name: 'Prize',
    key: 'campaigns.prize',
    className: '',
  },
  {
    name: 'Game',
    key: 'campaigns.game',
    className: '',
  },
];

const maskPlayerId = (id: string): string => {
  if (id.length <= 3) return id;
  return id.slice(0, 3) + '*'.repeat(id.length - 3);
};

const PrizeCell: React.FC<{ prize: string; currencyCode: string }> = ({ prize, currencyCode }) => {
  const { formattedAmount } = usePrizeFundFormatting(prize);
  return (
    <td className={`${tableStyles.tableCell} ${styles.prizeCell} ${styles.prizeAmount}`}>
      <span>
        {formattedAmount}&nbsp;
        <span className={styles.currencyCode}>{currencyCode}</span>
      </span>
    </td>
  );
};

const WinnersTab: React.FC = () => {
  const { t } = useTranslation();
  const selectedCampaign = useSelectedCampaign(PRIZE_POP_CAMPAIGN_TYPE);
  const { startGame } = useGameStartActions();
  const settings = useSettings();
  const { isPromo } = useCampaignStatus();

  const winsQuery = useCampaignWinsQuery();

  const winners = winsQuery.data?.wins || [];

  const formatWinnerDate = (dateString: string): string => {
    const date = new Date(dateString);
    const time = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const day = date.toLocaleDateString([], { day: '2-digit' });
    const monthAbbr = getMonthAbbr(date);
    const translatedMonth = t(`months.${monthAbbr}`, monthAbbr);
    return `${time}, ${day} ${translatedMonth}`;
  };

  // Loading state
  if (!selectedCampaign || winsQuery.isLoading) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.loaderContainer}>
          <LoadingIndicator show absolute />
        </div>
      </div>
    );
  }

  if (isPromo) {
    return (
      <CampaignTable columns={TABLE_COLUMNS} tableType="winners">
        <span className={styles.campaignNotActive}>{t('campaigns.campaign_not_started', "Тhe campaign hasn't started yet")}.</span>
      </CampaignTable>
    );
  }

  // No winners state
  if (winners.length === 0) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.noDataWrapper}>
          <p className={styles.noDataText}>{t('campaigns.no_winners', 'No winners yet')}.</p>
        </div>
      </div>
    );
  }

  const renderPlayerCell = (winner: IPrizePopWin) => (
    <td className={`${tableStyles.tableCell} ${styles.playerCell}`}>
      <span className={styles.playerCellId}>
        {winner.casinoPlayerId === settings.playerId ? t('generic_you', 'YOU').toUpperCase() : maskPlayerId(winner.casinoPlayerId)}
      </span>
      {(winner.wonDate || winner.date) && (
        <span className={styles.date}>{formatWinnerDate((winner.wonDate || winner.date) as string)}</span>
      )}
    </td>
  );

  const renderGameCell = (winner: IPrizePopWin) => (
    <td className={`${tableStyles.tableCell} ${styles.gameCell}`}>
      {winner.gameKey && (
        <div
          className={styles.gameInfo}
          onClick={() => startGame(winner.gameKey as string)}
        >
          <Image
            src={`/verticals/${winner.gameKey}`}
            alt={winner.gameKey}
            width={48}
            height={48}
            className={styles.gameThumbnail}
          />
        </div>
      )}
    </td>
  );

  return (
      <CampaignTable columns={TABLE_COLUMNS} tableType="winners">
        {winners.map((winner: IPrizePopWin, index: number) => (
          <tr
            key={index}
            className={`${tableStyles.tableRow} ${styles.winnersTableRow}`}
            data-is-current-player={winner.casinoPlayerId === settings.playerId}
          >
            {renderPlayerCell(winner)}
            <PrizeCell prize={winner.prize} currencyCode={winner.currencyCode} />
            {renderGameCell(winner)}
          </tr>
        ))}
      </CampaignTable>
  );
};

export default memo(WinnersTab);
